import React from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

import { CHECKLIST_STATUS_STYLES } from '../../constants';
import { getEffectiveChecklistStatus, isRowApproved, isRowManuallyApproved } from '../../manualOverridePersistence';
import ApprovalActionButton from '../../reportCards/ApprovalActionButton';
import ApprovalStatusChip from '../../reportCards/ApprovalStatusChip';
import ChecklistStatusDot from '../../reportCards/ChecklistStatusDot';
import EmptyTableRow from '../../reportCards/EmptyTableRow';
import HiddenApprovedFooter from '../../reportCards/HiddenApprovedFooter';
import ReportCard from '../../reportCards/ReportCard';
import ReportNote from '../../reportCards/ReportNote';
import SortTableCell from '../../reportCards/SortTableCell';
import { formatOptionalNumber } from '../../shared/format';
import type { ManualOverrideState } from '../../types';

import { CAPACITY_NAME_COLUMN_WIDTH, EGS_TABLE_COLUMN_WIDTHS } from './constants';
import type { EgsCapacityRowView } from './types';

interface Props {
  hiddenApprovedCount: number;
  manualOverrides: ManualOverrideState;
  rows: Array<EgsCapacityRowView>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function CapacityReportCard({
  hiddenApprovedCount,
  manualOverrides,
  rows,
  showAutoApproved,
  onApprovalChange,
  onUnapprovalChange,
}: Props) {
  return (
    <ReportCard>
      <ReportNote
        icon={<InfoOutlinedIcon />}
        text={
          <>
            <strong>EGS capacity check:</strong> auto-approve when{' '}
            <strong>Max Room Capacity = Included Capacity + EGS Capacity</strong>, and EGS Capacity is not negative.
          </>
        }
      />
      <TableContainer>
        <Table size="small" sx={{ minWidth: 1008, tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <SortTableCell sx={{ width: CAPACITY_NAME_COLUMN_WIDTH }} labelSx={{ fontSize: 12 }}>
                Deal Option / Room Asset
              </SortTableCell>
              <SortTableCell
                sx={{ width: EGS_TABLE_COLUMN_WIDTHS.col3 }}
                labelSx={{ whiteSpace: 'normal', fontSize: 12 }}
              >
                Included Capacity
              </SortTableCell>
              <SortTableCell
                sx={{ width: EGS_TABLE_COLUMN_WIDTHS.col4 }}
                labelSx={{ whiteSpace: 'normal', fontSize: 12 }}
              >
                Max Room Capacity
              </SortTableCell>
              <SortTableCell
                sx={{ width: EGS_TABLE_COLUMN_WIDTHS.col5 + EGS_TABLE_COLUMN_WIDTHS.check }}
                labelSx={{ whiteSpace: 'normal', fontSize: 12 }}
              >
                EGS Capacity
              </SortTableCell>
              <TableCell sx={{ fontSize: 12, width: EGS_TABLE_COLUMN_WIDTHS.action }}>Action</TableCell>
              <TableCell sx={{ width: EGS_TABLE_COLUMN_WIDTHS.chevron }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyTableRow colSpan={6} label="No EGS capacity rows match this view." />
            ) : (
              rows.map((row) => {
                const manuallyApproved = isRowManuallyApproved(row, manualOverrides);
                const approved = isRowApproved(row, manualOverrides);

                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={[{ '& td': { py: 1.5 } }, approved ? CHECKLIST_STATUS_STYLES.approvedRow : undefined]}
                  >
                    <TableCell sx={{ width: CAPACITY_NAME_COLUMN_WIDTH }}>
                      <Typography variant="body2" fontWeight={500}>
                        {row.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {row.roomName}
                      </Typography>
                      <ApprovalStatusChip approved={approved} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatOptionalNumber(row.includedCapacity)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatOptionalNumber(row.maxRoomCapacity)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ChecklistStatusDot
                          approvalType={manuallyApproved ? 'manual' : 'auto'}
                          status={getEffectiveChecklistStatus(row, manualOverrides)}
                          band={approved ? 'green' : undefined}
                          hideLabel
                        />
                        <Typography variant="body2">{formatOptionalNumber(row.egsCapacity)}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <ApprovalActionButton
                        approved={approved}
                        onApprove={() => onApprovalChange(row.id)}
                        onUnapprove={() => onUnapprovalChange(row.id)}
                      />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <HiddenApprovedFooter count={hiddenApprovedCount} noun="EGS capacity row" showAutoApproved={showAutoApproved} />
    </ReportCard>
  );
}
